import { useEffect, useState } from "react";
import { Send, Link2, Check, Share2 } from "lucide-react";

const CANONICAL_URL = "https://hantavirus.international";
const SHARE_TITLE = "Hantavirus International — Live Outbreak Map";
const SHARE_TEXT =
  "Help accurate information reach more people. Share the live hantavirus outbreak tracker.";

export default function SocialShare() {
  const [copied, setCopied] = useState(false);
  // Use canonical URL during SSR + first client render to avoid hydration
  // mismatch (React #418), then swap to actual page URL after mount.
  const [url, setUrl] = useState(CANONICAL_URL);

  useEffect(() => {
    setUrl(window.location.origin + window.location.pathname);
  }, []);

  const getUrl = () => url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(getUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  const native = async () => {
    const url = getUrl();
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({ title: SHARE_TITLE, text: SHARE_TEXT, url });
        return;
      } catch {
        /* cancelled */
      }
    }
    copy();
  };

  const enc = encodeURIComponent;

  const links = [
    {
      key: "x",
      label: "X",
      brand: "#000000",
      href: `https://twitter.com/intent/tweet?text=${enc(SHARE_TITLE)}&url=${enc(url)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M18.244 2H21l-6.52 7.45L22 22h-6.797l-5.32-6.94L3.8 22H1.04l6.98-7.97L1.6 2h6.96l4.81 6.36L18.244 2Zm-1.19 18h1.88L7.05 4H5.06l11.994 16Z" />
        </svg>
      ),
    },
    {
      key: "telegram",
      label: "Telegram",
      brand: "#229ED9",
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(SHARE_TITLE)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 11.95c-.88-.25-.89-.86.2-1.3L19.07 4.7c.73-.33 1.43.18 1.15 1.3l-2.6 12.27c-.19.91-.74 1.13-1.5.71l-4.14-3.06-2 1.94c-.23.23-.42.42-.86.42z" />
        </svg>
      ),
    },
    {
      key: "reddit",
      label: "Reddit",
      brand: "#FF4500",
      href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(SHARE_TITLE)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M22 12.07c0-1.21-.99-2.2-2.2-2.2-.6 0-1.14.24-1.54.62-1.5-1.04-3.54-1.71-5.81-1.79l1.18-3.71 3.18.75c.04.92.79 1.66 1.71 1.66.95 0 1.71-.78 1.71-1.72S19.46 4 18.52 4c-.68 0-1.27.4-1.54.97l-3.55-.83a.42.42 0 0 0-.5.29l-1.32 4.16c-2.3.06-4.36.73-5.88 1.79-.4-.39-.95-.63-1.55-.63-1.21 0-2.18 1-2.18 2.21 0 .89.52 1.65 1.27 2-.04.23-.06.46-.06.7 0 3.55 4.13 6.42 9.22 6.42s9.21-2.87 9.21-6.42c0-.24-.02-.47-.06-.7.74-.35 1.26-1.11 1.26-2zM7.04 13.6c0-.95.78-1.72 1.72-1.72.95 0 1.72.78 1.72 1.72 0 .95-.78 1.72-1.72 1.72-.95 0-1.72-.78-1.72-1.72zm9.7 4.21c-1.18 1.18-3.43 1.27-4.09 1.27-.66 0-2.91-.09-4.09-1.27a.45.45 0 1 1 .63-.63c.74.74 2.34 1.01 3.46 1.01 1.12 0 2.71-.27 3.46-1.01a.45.45 0 0 1 .63.63zm-.41-2.49c-.95 0-1.72-.78-1.72-1.72 0-.95.78-1.72 1.72-1.72s1.72.78 1.72 1.72c0 .94-.78 1.72-1.72 1.72z" />
        </svg>
      ),
    },
    {
      key: "wa",
      label: "WhatsApp",
      brand: "#25D366",
      href: `https://wa.me/?text=${enc(SHARE_TITLE + " " + url)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M20.5 3.5A10.45 10.45 0 0 0 12 0C6.2 0 1.5 4.7 1.5 10.5c0 1.85.49 3.65 1.42 5.24L1 23l7.45-1.94a10.5 10.5 0 0 0 5.05 1.29c5.8 0 10.5-4.7 10.5-10.5 0-2.8-1.09-5.43-3.5-7.35zM12 20.6c-1.6 0-3.16-.43-4.52-1.24l-.32-.19-4.42 1.15 1.18-4.3-.21-.34A8.6 8.6 0 0 1 3.4 10.5C3.4 5.75 7.25 1.9 12 1.9s8.6 3.85 8.6 8.6S16.75 20.6 12 20.6zm4.94-6.43c-.27-.14-1.6-.79-1.84-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.13-.42-2.16-1.33-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.4-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.02-.22-.53-.45-.45-.61-.46l-.52-.01a1 1 0 0 0-.72.34c-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.11 2.81.14.18 1.92 2.93 4.65 4.11.65.28 1.16.45 1.55.57.65.21 1.24.18 1.71.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.32z" />
        </svg>
      ),
    },
    {
      key: "fb",
      label: "Facebook",
      brand: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.7c0-.93.26-1.56 1.6-1.56h1.7V4.27c-.3-.04-1.32-.13-2.5-.13-2.48 0-4.18 1.51-4.18 4.29v2.37H7.5V14h2.62v8h3.38z" />
        </svg>
      ),
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      brand: "#0A66C2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
          <path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V10.5H5.67v7.84h2.67zM7 9.34a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9V13.9c0-2.47-1.32-3.62-3.08-3.62-1.43 0-2.07.79-2.43 1.34V10.5h-2.66c.04.75 0 7.84 0 7.84h2.66v-4.38c0-.24.02-.48.09-.65.19-.48.63-.97 1.36-.97.96 0 1.34.73 1.34 1.79v4.21h2.72z" />
        </svg>
      ),
    },
  ];

  return (
    <section
      aria-label="Share this tracker"
      className="border-t border-border bg-background px-4 md:px-6 py-12"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-hvi-primary/40 bg-hvi-primary/5 mb-4">
            <Share2 className="w-3.5 h-3.5 text-hvi-primary" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-hvi-primary font-bold">
              Share
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 tracking-tight">
            Help spread accurate information
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-xl">
            Misinformation moves fast. Share this live tracker so verified WHO, CDC and UKHSA
            data reaches more people.
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 md:gap-3 mb-3">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              aria-label={`Share on ${l.label}`}
              title={`Share on ${l.label}`}
              style={{ ["--brand" as any]: l.brand }}
              className="group relative flex flex-col items-center justify-center gap-2 py-4 px-2 border border-border bg-card hover:bg-[var(--brand)] hover:border-[var(--brand)] hover:text-white transition-all duration-200 hover:-translate-y-0.5"
            >
              <span className="text-muted-foreground group-hover:text-white transition-colors">
                {l.icon}
              </span>
              <span className="text-[10px] md:text-xs uppercase tracking-wider font-bold text-foreground group-hover:text-white">
                {l.label}
              </span>
            </a>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={copy}
            aria-label={copied ? "Link copied" : "Copy link"}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border transition-all text-xs uppercase tracking-wider font-bold ${
              copied
                ? "border-hvi-primary bg-hvi-primary/10 text-hvi-primary"
                : "border-border bg-card text-foreground hover:border-hvi-primary hover:text-hvi-primary"
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            {copied ? "Link copied" : "Copy link"}
          </button>
          <button
            onClick={native}
            aria-label="More share options"
            className="sm:flex-none flex items-center justify-center gap-2 px-4 py-3 border border-border bg-card text-foreground hover:border-hvi-primary hover:text-hvi-primary transition-all text-xs uppercase tracking-wider font-bold sm:hidden"
          >
            <Send className="w-4 h-4" />
            More
          </button>
        </div>
      </div>
    </section>
  );
}
